import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/arbiter/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/arbiter/flex";
import { RedoSpinner } from "@redotech/arbiter/loading/redo-spinner";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import LockIcon from "@redotech/redo-icons/lock-unlocked-01.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useState } from "react";

export const ActionAuthorizePage = memo(function ActionAuthorizePage() {
  const { t } = useTranslation();
  const [token] = useState<string | undefined>(readTokenFromHash);
  const rpcClient = useRequiredContext(RpcClientContext);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) {
      return;
    }
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  }, []);

  const statusQuery = useQuery({
    queryKey: token
      ? statusQueryKey(token)
      : ["action-authorize", "status", "missing"],
    queryFn: async () => {
      if (!token) {
        throw new Error("Authorization token missing from URL");
      }
      return rpcClient.getActionAuthorizationStatus({ token });
    },
    enabled: Boolean(token),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!token) {
        throw new Error("Authorization token missing from URL");
      }
      return rpcClient.confirmActionAuthorization({ token });
    },
    onSuccess: () => {
      if (token) {
        void queryClient.invalidateQueries({ queryKey: statusQueryKey(token) });
      }
    },
  });

  if (!token) {
    return <ExpiredCard />;
  }

  if (statusQuery.isPending) {
    return (
      <PageShell>
        <Flex
          align="center"
          data-ui="action-authorize-loading"
          dir="column"
          gap="lg"
          justify="center"
          py="6xl"
        >
          <RedoSpinner size="lg" />
          <Text fontSize="sm" textColor="secondary">
            {t("Loading…")}
          </Text>
        </Flex>
      </PageShell>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    return <ExpiredCard />;
  }

  if (confirmMutation.data?.status === "confirmed") {
    return (
      <ActionAuthorizeCard dataUi="action-authorize-confirmed">
        <RedoFeaturedIcon
          color="success"
          Icon={CheckCircleIcon}
          size="xl"
          type="outline"
        />
        <Flex align="center" dir="column" gap="sm">
          <Text fontSize="xl" fontWeight="semibold" textAlign="center">
            {t("Security check complete")}
          </Text>
          <Text fontSize="sm" textAlign="center" textColor="secondary">
            {t("Return to your conversation to continue.")}
          </Text>
        </Flex>
      </ActionAuthorizeCard>
    );
  }

  switch (statusQuery.data.status) {
    case "pending": {
      const confirmError = confirmMutation.isError;
      return (
        <ActionAuthorizeCard dataUi="action-authorize-pending">
          <RedoFeaturedIcon
            color="brand"
            Icon={LockIcon}
            size="xl"
            type="outline"
          />
          <Flex align="center" dir="column" gap="sm">
            <Text fontSize="xl" fontWeight="semibold" textAlign="center">
              {t("Security check")}
            </Text>
            <Text fontSize="sm" textAlign="center" textColor="secondary">
              {t(
                "Confirm it's you so we can keep helping you over SMS. This magic link is single-use and expires soon.",
              )}
            </Text>
          </Flex>
          <Flex dir="column" gap="md" w="full">
            <RedoButton
              hierarchy="primary"
              onClick={() => confirmMutation.mutate()}
              pending={confirmMutation.isPending}
              size="lg"
              text={
                confirmMutation.isPending
                  ? t("Confirming…")
                  : t("Confirm it's me")
              }
              uiSelector="action-authorize-confirm-button"
            />
            {confirmError && (
              <Text fontSize="sm" textAlign="center" textColor="error">
                {t("Something went wrong. Please try again.")}
              </Text>
            )}
          </Flex>
        </ActionAuthorizeCard>
      );
    }
    case "already_confirmed": {
      return (
        <ActionAuthorizeCard dataUi="action-authorize-already-confirmed">
          <RedoFeaturedIcon
            color="success"
            Icon={CheckCircleIcon}
            size="xl"
            type="outline"
          />
          <Flex align="center" dir="column" gap="sm">
            <Text fontSize="xl" fontWeight="semibold" textAlign="center">
              {t("You've already completed this security check")}
            </Text>
            <Text fontSize="sm" textAlign="center" textColor="secondary">
              {t(
                "Return to your conversation to continue receiving help over SMS.",
              )}
            </Text>
          </Flex>
        </ActionAuthorizeCard>
      );
    }
    case "expired_or_unknown": {
      return <ExpiredCard />;
    }
  }
});

const PageShell = memo(function PageShell({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Flex
      align="center"
      bgColor="secondary"
      data-ui="action-authorize-page"
      dir="column"
      grow={1}
      height="screen"
      justify="center"
      p="2xl"
      w="full"
    >
      {children}
    </Flex>
  );
});

const ActionAuthorizeCard = memo(function ActionAuthorizeCard({
  dataUi,
  children,
}: {
  dataUi: string;
  children: React.ReactNode;
}) {
  return (
    <PageShell>
      <Flex
        align="center"
        bgColor="primary"
        borderColor="secondary"
        borderRadius="xl"
        borderStyle="solid"
        borderWidth="1px"
        data-ui={dataUi}
        dir="column"
        gap="2xl"
        maxW="sm"
        p="4xl"
        shadow="md"
        w="full"
      >
        {children}
      </Flex>
    </PageShell>
  );
});

const ExpiredCard = memo(function ExpiredCard() {
  const { t } = useTranslation();
  return (
    <ActionAuthorizeCard dataUi="action-authorize-expired">
      <RedoFeaturedIcon
        color="warning"
        Icon={AlertTriangleIcon}
        size="xl"
        type="outline"
      />
      <Flex align="center" dir="column" gap="sm">
        <Text fontSize="xl" fontWeight="semibold" textAlign="center">
          {t("This security check link is no longer valid")}
        </Text>
        <Text fontSize="sm" textAlign="center" textColor="secondary">
          {t(
            "This magic link may have expired or already been used. Return to your conversation and ask for a new link if you still need help.",
          )}
        </Text>
      </Flex>
    </ActionAuthorizeCard>
  );
});

function statusQueryKey(token: string) {
  return ["action-authorize", "status", token];
}

// Token is in the URL fragment (#<token>) so it never reaches servers, CDN
// access logs, or Referer headers sent to third-party scripts. Keep it in
// the fragment.
function readTokenFromHash(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const hash = window.location.hash;
  if (!hash.startsWith("#")) {
    return undefined;
  }
  const token = hash.slice(1);
  return token.length > 0 ? token : undefined;
}
